import cn from 'classnames';
import { useState, useTransition } from 'react';
import { OSIcon } from '#components/atoms/cards/os-card/OSIcon.tsx';
import { RadioGroup } from '#components/patterns/forms/radio/index.ts';
import { RadioCard } from '#components/patterns/forms/radio/RadioCard.tsx';
import type { ArchOption, OSGroup } from '#dn-types/downloads.ts';
import { ArchitectureSelect } from './architecture-select/ArchitectureSelect.tsx';
import { EnrollmentCodeCopyButton } from './EnrollmentCodeCopyButton.tsx';
import type { DownloadsMap } from './types.ts';
import styles from './ArchitecturePicker.module.css';

export const OS_GROUPS = ['Linux', 'macOS', 'Windows', 'Mobile', 'Other'] as const satisfies OSGroup[];
export type ArchPickerOptions = Exclude<ArchOption, 'macos-universal-server' | 'macos-universal-server-dmg'> | '';

type Props = {
  downloads: DownloadsMap;
  changeAction: (osGroup: OSGroup, arch: ArchOption | '') => void;
  includeMobile?: boolean;
  enrollmentToken?: string;
  className?: string;
};

export function ArchitecturePicker({ downloads, changeAction, includeMobile, className, enrollmentToken }: Props) {
  const [, startTransition] = useTransition();
  const [group, setGroup] = useState<OSGroup>();
  const [arch, setArch] = useState<ArchPickerOptions | ''>('');

  function calculateBaseArch(g: OSGroup) {
    return g === 'macOS' ? 'macos-universal-desktop' : '';
  }

  return (
    <>
      <div className={cn(styles.ArchitecturePicker_options, className)}>
        <RadioGroup
          name="architecture"
          onChange={(e) => {
            const g = e.currentTarget.value as OSGroup;
            const a = calculateBaseArch(g);
            setGroup(g);
            setArch(a);
            startTransition(() => {
              changeAction(g, a);
            });
          }}
        >
          {OS_GROUPS.filter((os) => (includeMobile ? true : os !== 'Mobile')).map((os) => (
            <RadioCard value={os} key={os} className={styles.ArchitecturePicker_osCard}>
              <h3 className={cn('h5', styles.ArchitecturePicker_osCardTitle)}>
                <OSIcon os={os} />
                {os}
              </h3>
            </RadioCard>
          ))}
        </RadioGroup>
      </div>
      {enrollmentToken ?
        <div className={cn('p-md mb-40 color-body', styles.ArchitecturePicker_advancedOption)}>
          <div className="shrink-0">Advanced option:</div> <EnrollmentCodeCopyButton token={enrollmentToken} />
        </div>
      : null}
      {/** Don't ask for an arch on macOS (single artifact), Linux (chosen inside the binary-download step), or Mobile (both stores are linked) */}
      {group && !(group === 'macOS' || group === 'Linux' || group === 'Mobile' || arch === 'macos-universal-desktop') ?
        <ArchitectureSelect
          className="mb-40"
          downloads={downloads}
          group={group}
          value={arch}
          // Select only calls onChange for real selection changes (never on mount or as an echo
          // of the `value` prop), so the change can be reported without any reentrancy guards.
          onChange={(value: ArchPickerOptions | '') => {
            setArch(value);
            startTransition(() => {
              changeAction(group, value);
            });
          }}
        />
      : null}
    </>
  );
}
